<script lang="ts" setup>
import { computed } from 'vue'
import { v4 as uuid } from 'uuid'
import { theme as brandTheme } from '@/config/brands'

// props 签名与 RecommendPage.default.vue / RecommendPage.flipnovel.vue 完全一致，
// 数据、埋点、跳转全部由 src/pages/recommend/index.vue 提供，本组件只负责渲染。
const props = defineProps<{
  config: any
  chapterTheme: any
  statusDisplay: string
  statusDescription: string
  fetching: boolean
  error: any
  recommendations: any[]
  allData: any
  total: number
  pageSize: number
  goToCollection: () => void
  handleBookClick: (book: any) => void
}>()

// 完读页固定浅底 #FBF6FB（不跟随阅读器主题色 chapterTheme），与设计稿一致
const pageStyle = computed(() => ({
  backgroundColor: brandTheme.bgGlobal || '#FBF6FB',
}))

// 区块标题：接口 showName 优先，缺省用 i18n 文案（逻辑与另两版一致）
const sectionTitle = computed(() => props.allData?.showName || props.config?.texts?.youMightAlsoLike)
</script>

<template>
  <div class="nm-recommend h-100dvh overflow-scroll" :style="pageStyle">
    <!-- 返回：复用 BasicNavbar（内含 useGotoBack），仅调整留白/图标尺寸与颜色 -->
    <BasicNavbar class="nm-navbar pl-8px pt-8px" :icon-style="{ width: '24px', height: '24px', color: '#2B1B34' }" />

    <!-- END / ONGOING 区（左右 16、下 24；上留白 = 固定导航条 40px + 设计间距 24px） -->
    <div class="px-16px pb-24px pt-64px">
      <div class="flex items-center justify-center">
        <span class="nm-rule" />
        <span class="nm-end mx-12px">{{ statusDisplay }}</span>
        <span class="nm-rule" />
      </div>
      <p class="mt-8px text-center text-13px lh-18px text-n-text-2">
        {{ statusDescription }}
      </p>
    </div>

    <div class="px-16px pb-32px">
      <!-- Loading -->
      <div v-if="fetching" class="py-24px flex items-center justify-center">
        <BasicSpinner class="mx-auto block" />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="py-24px text-13px text-center text-n-text-3">
        {{ config.texts.failedToLoad }}
      </div>

      <template v-else>
        <!-- 区块标题 + More -->
        <div v-if="recommendations && recommendations.length" class="flex items-center justify-between">
          <p class="nm-section-title">
            {{ sectionTitle }}<span class="ml-4px">💗</span>
          </p>
          <div
            v-if="total > pageSize"
            class="flex items-center cursor-pointer text-n-primary"
            @click="goToCollection"
          >
            <span class="text-12px font-700 lh-16px">{{ config.texts.more }}</span>
            <i-lucide-chevron-right class="ml-2px w-12px h-12px" />
          </div>
        </div>

        <!-- 推荐列表：行间距 14 -->
        <div class="mt-16px space-y-14px">
          <div
            v-for="(item, index) in recommendations"
            :key="index"
            class="nm-item"
            @click="handleBookClick(item)"
          >
            <BizBookCover
              :id="uuid()"
              :src="item.contentCoverUrl || ''"
              :height-rate="136"
              :extra="{
                module_name: allData.showName,
                module_id: allData.id,
                module_type: allData.columnType,
                author: item.authorInfo?.author,
                ...item,
              }"
              class="nm-cover rounded-10px"
            />

            <div class="min-w-0 flex-1">
              <p class="nm-item-title line-clamp-1">
                {{ item.contentName?.replace(/&nbsp;/g, ' ') ?? '' }}
              </p>

              <div v-if="item.tagList && item.tagList.length" class="nm-chips mt-6px">
                <BizShowTags :tags="(item.tagList || []).slice(0, 2)" />
              </div>

              <p class="nm-item-desc mt-6px line-clamp-2 break-anywhere">
                {{ item.introduce }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.nm-recommend {
  background-image: none !important;
}

/* 顶部返回区留白：BasicNavbar 自带 p-2(8px)，再补 8px 得到设计稿的 16 */
.nm-navbar {
  background: transparent;
}

/* END 两侧短横线 28x2 圆角 1 */
.nm-rule {
  display: block;
  width: 28px;
  height: 2px;
  border-radius: 1px;
  background-color: #d9c8df;
}

/* END：Baloo 2 / 28 / 700 / ink-1 */
.nm-end {
  font-family: 'Baloo 2', 'Nunito Sans', system-ui, sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
  letter-spacing: 0.5px;
  color: #2b1b34;
}

/* 区块标题：Baloo 2 / 17 / 700 / ink-1 */
.nm-section-title {
  font-family: 'Baloo 2', 'Nunito Sans', system-ui, sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  color: #2b1b34;
}

/* 列表行：封面 72x98 + 右列，横向间距 14（用 margin 而非 flex gap，兼容 iOS 14.4） */
.nm-item {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
}

.nm-cover {
  width: 72px;
  flex-shrink: 0;
  margin-right: 14px;
}

.nm-item-title {
  font-size: 14px;
  font-weight: 800;
  line-height: 19px;
  color: #2b1b34;
}

.nm-item-desc {
  font-size: 12px;
  line-height: 1.45;
  color: #71607e;
}

/* chips：复用 BizShowTags，仅覆写皮肤 —— 奇数粉(pink-soft)、偶数紫(purple-soft) */
.nm-chips :deep(.tag-item),
.nm-chips :deep(.ellipsis-tag) {
  height: 18px;
  padding: 0 8px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
}

.nm-chips :deep(.tag-item:nth-child(odd)) {
  background-color: #fdeff6;
  color: #f0509b;
}

.nm-chips :deep(.tag-item:nth-child(even)) {
  background-color: #f3edfe;
  color: #7c4de8;
}

.nm-chips :deep(.ellipsis-tag) {
  background-color: #f3edfe;
  color: #7c4de8;
}
</style>
